import React, { useState } from 'react';
import { Form, Dropdown, Badge } from 'react-bootstrap';

const SearchableSelect = ({ 
    options = [], 
    value, 
    onChange, 
    onDisabledSelect,
    placeholder = "Seleccione...", 
    disabled = false,
    size,
    defaultOptionLabel = "-- Sin Asignar / Central --",
    showDefaultOption = true,
    searchPlaceholder
}) => {
    const [search, setSearch] = useState('');
    
    // Asegurar que options sea un array y filtrar nulos
    const safeOptions = Array.isArray(options) ? options.filter(o => o !== null) : [];
    
    // El objeto seleccionado puede venir por id o ser el objeto mismo
    const selectedItem = safeOptions.find(o => String(o.id) === String(value));
    
    // Priorizamos full_name para Requesters/Passengers/Drivers y name para Clients/Areas
    const getLabel = (item) => item.full_name || item.name || item.internal_code || placeholder;
    const label = selectedItem ? getLabel(selectedItem) : placeholder;
    
    // Filtramos opciones buscando en múltiples campos comunes
    const filteredOptions = safeOptions.filter(o => {
        if (!o) return false;
        const searchText = search.toLowerCase();
        const name = (o.full_name || o.name || '').toLowerCase();
        const rut = (o.rut || '').toLowerCase();
        const code = (o.internal_code || '').toLowerCase();
        const plate = (o.plate || '').toLowerCase();
        const reason = (o.unavailability_reason || '').toLowerCase();
        return name.includes(searchText) || rut.includes(searchText) || code.includes(searchText) || plate.includes(searchText) || reason.includes(searchText);
    });

    // Separar disponibles de no disponibles si la lista tiene items con estado de disponibilidad
    const hasAvailabilityStatus = safeOptions.some(o => o.is_available !== undefined || o.disabled !== undefined);
    const availableOptions = hasAvailabilityStatus 
        ? filteredOptions.filter(o => o.is_available !== false && !o.disabled) 
        : filteredOptions;
    const unavailableOptions = hasAvailabilityStatus 
        ? filteredOptions.filter(o => o.is_available === false || o.disabled) 
        : [];

    const recommendedOptions = availableOptions.filter(o => o.is_recommended);
    const otherAvailableOptions = availableOptions.filter(o => !o.is_recommended);

    const handleItemClick = (opt) => {
        if (opt.is_available === false || opt.disabled) {
            if (onDisabledSelect) {
                onDisabledSelect(opt);
            }
            return;
        }
        onChange(opt.id || '');
        setSearch('');
    };

    return (
        <Dropdown className="w-100">
            <Dropdown.Toggle 
                variant="outline-secondary" 
                size={size}
                className="w-100 text-start d-flex justify-content-between align-items-center bg-white text-dark shadow-sm" 
                style={{ 
                    border: '1px solid #ced4da', 
                    minHeight: size === 'sm' ? '31px' : '38px', 
                    height: size === 'sm' ? '31px' : undefined,
                    paddingTop: size === 'sm' ? '2px' : undefined,
                    paddingBottom: size === 'sm' ? '2px' : undefined,
                    paddingRight: '30px', 
                    position: 'relative',
                    fontSize: size === 'sm' ? '0.875rem' : undefined
                }}
                disabled={disabled}
            >
                <span className="text-truncate" style={{ maxWidth: '90%' }}>
                    {selectedItem ? (
                        <>
                            {selectedItem.internal_code && <span className="badge bg-dark me-2">{selectedItem.internal_code}</span>}
                            {selectedItem.full_name || selectedItem.name}
                            {selectedItem.plate && <span className="ms-2 text-muted">({selectedItem.plate})</span>}
                            {selectedItem.is_recommended && (
                                <Badge bg="warning" text="dark" className="ms-2 border border-warning">
                                    ⭐ Sugerido
                                </Badge>
                            )}
                            {(selectedItem.is_available === false || selectedItem.is_forced) && (
                                <Badge bg="warning" text="dark" className="ms-2 border border-warning">
                                    ⚠️ Asignado Excepcional
                                </Badge>
                            )}
                        </>
                    ) : <span className="text-muted">{label}</span>}
                </span>
                {selectedItem && (
                    <span 
                        onClick={(e) => { e.stopPropagation(); onChange(''); }}
                        style={{ 
                            position: 'absolute', 
                            right: '35px', 
                            cursor: 'pointer', 
                            opacity: 0.6,
                            padding: '0 5px'
                        }}
                        title="Limpiar Selección"
                    >
                        ✕
                    </span>
                )}
            </Dropdown.Toggle>
            <Dropdown.Menu 
                className="w-100 shadow" 
                style={{ maxHeight: '350px', overflowY: 'auto', zIndex: 1060 }}
                flip={true}
            >
                <div className="p-2 sticky-top bg-white border-bottom">
                    <Form.Control 
                        size="sm" 
                        placeholder={searchPlaceholder || "🔍 Buscar por nombre, móvil, patente..."} 
                        value={search} 
                        onChange={(e) => setSearch(e.target.value)} 
                        autoFocus 
                        onClick={(e) => e.stopPropagation()} 
                    />
                </div>
                {showDefaultOption && (
                    <>
                        <Dropdown.Item onClick={() => { onChange(''); setSearch(''); }} className="text-primary fw-bold">
                            {defaultOptionLabel}
                        </Dropdown.Item>
                        <Dropdown.Divider />
                    </>
                )}

                {/* GRUPO 1: RECOMENDADOS PARA TU SCORE */}
                {hasAvailabilityStatus && recommendedOptions.length > 0 && (
                    <>
                        <Dropdown.Header className="text-warning fw-bold small py-1 text-uppercase bg-dark d-flex align-items-center justify-content-between">
                            <span>⭐ Sugeridos para tu Score ({recommendedOptions.length})</span>
                            <small className="text-warning-50 fw-normal" style={{ fontSize: '10px' }}>Baja carga mensual</small>
                        </Dropdown.Header>
                        {recommendedOptions.map(opt => (
                            <Dropdown.Item 
                                key={opt.id} 
                                onClick={() => handleItemClick(opt)}
                                active={String(opt.id) === String(value)}
                                style={{ backgroundColor: '#fffbeb', borderLeft: '3px solid #f59e0b' }}
                            >
                                <div className="d-flex justify-content-between align-items-center">
                                    <span>
                                        <strong className="text-warning me-1">⭐</strong>
                                        {opt.internal_code && <strong className="me-2">[{opt.internal_code}]</strong>}
                                        {opt.full_name || opt.name}
                                        {opt.recommendation_badge && (
                                            <Badge bg="warning" text="dark" className="ms-2" style={{ fontSize: '9px' }}>
                                                {opt.recommendation_badge}
                                            </Badge>
                                        )}
                                    </span>
                                    <div className="d-flex gap-2 align-items-center">
                                        {opt.trips_last_30d !== undefined && (
                                            <small className="badge bg-light text-muted border">{opt.trips_last_30d} v. (30d)</small>
                                        )}
                                        {opt.plate && <small className="badge bg-light text-dark border">{opt.plate}</small>}
                                    </div>
                                </div>
                            </Dropdown.Item>
                        ))}
                        <Dropdown.Divider />
                    </>
                )}

                {/* GRUPO 2: DISPONIBLES HABITUALES */}
                {hasAvailabilityStatus && otherAvailableOptions.length > 0 && (
                    <Dropdown.Header className="text-success fw-bold small py-1 text-uppercase bg-light">
                        🟢 Otros Disponibles ({otherAvailableOptions.length})
                    </Dropdown.Header>
                )}

                {otherAvailableOptions.map(opt => (
                    <Dropdown.Item 
                        key={opt.id} 
                        onClick={() => handleItemClick(opt)}
                        active={String(opt.id) === String(value)}
                    >
                        <div className="d-flex justify-content-between align-items-center">
                            <span>
                                {opt.internal_code && <strong className="me-2">[{opt.internal_code}]</strong>}
                                {opt.full_name || opt.name}
                            </span>
                            <div className="d-flex gap-2 align-items-center">
                                {opt.plate && <small className="badge bg-light text-dark border">{opt.plate}</small>}
                                {opt.rut && <small className="text-muted small">{opt.rut}</small>}
                            </div>
                        </div>
                    </Dropdown.Item>
                ))}

                {/* GRUPO 2: NO DISPONIBLES PARA ESTA FECHA / HORA */}
                {hasAvailabilityStatus && unavailableOptions.length > 0 && (
                    <>
                        <Dropdown.Header className="text-danger fw-bold small py-1 mt-2 text-uppercase bg-danger-subtle border-top">
                            ⛔ No Disponibles para esta Fecha / Hora ({unavailableOptions.length})
                        </Dropdown.Header>
                        {unavailableOptions.map(opt => (
                            <Dropdown.Item 
                                key={opt.id} 
                                onClick={() => handleItemClick(opt)}
                                active={String(opt.id) === String(value)}
                                className="bg-light-subtle opacity-75"
                                style={{ borderLeft: '3px solid #dc3545' }}
                            >
                                <div className="d-flex justify-content-between align-items-start gap-2">
                                    <div>
                                        <div className="text-dark">
                                            {opt.internal_code && <strong className="me-1">[{opt.internal_code}]</strong>}
                                            {opt.full_name || opt.name}
                                            {opt.plate && <small className="text-muted ms-1">({opt.plate})</small>}
                                        </div>
                                        {opt.unavailability_reason && (
                                            <div className="text-danger small fw-semibold mt-0.5">
                                                ⚠️ {opt.unavailability_reason}
                                            </div>
                                        )}
                                    </div>
                                    <Badge bg={(opt.has_capacity_block || opt.has_booking_only_block) ? 'dark' : 'danger'} className="text-uppercase" style={{ fontSize: '9px' }}>
                                        {(opt.has_capacity_block || opt.has_booking_only_block) ? '🚫 Bloqueado' : 'Excepcional'}
                                    </Badge>
                                </div>
                            </Dropdown.Item>
                        ))}
                    </>
                )}

                {filteredOptions.length === 0 && (
                    <div className="p-3 text-center text-muted small">No se encontraron resultados</div>
                )}
            </Dropdown.Menu>
        </Dropdown>
    );
};

export default SearchableSelect;
